/**
 * parsePrelimAssignments — parse the "Prelim Anes Assignments" Google Sheet
 * into raw assignment rows, keeping ONLY one facility's room family.
 *
 * This is the live daily board published by the anesthesia department (one new
 * file per day, titled "Prelim Anes Assignments M.D.YY"). It is one flat sheet
 * for all AdventHealth Tampa-area hospitals; each import pass keeps one campus.
 * The OR name's PREFIX encodes the hospital:
 *   Tampa         → TOR*, plus shared Tampa rooms (CT, ENDO*, CIE*, CV*, NI, EP*, CL*, WC<digit>)
 *   Carrollwood   → COR*, CAR*
 *   Wesley Chapel → WOR*, "WC <label>"
 *
 * Footer/meta rows ("VAC:", "NOTES:", "*Meetings*", the "----- Marker row"
 * line) all carry a non-room col-A value, so the family filter drops them.
 *
 * Pure function — no network, no Firebase. Input is the raw 2D `values` array
 * from the Sheets API `spreadsheets.values.get` (ragged rows allowed; strings).
 *
 * ─── COLUMNS ARE READ FROM THE HEADER ROW, NOT FROM THEIR POSITION ──────────
 *
 * ⚠️ THE SHEET IS EDITED ALL DAY BY PEOPLE RUNNING AN OR. Between two reads of
 * the same file on 2026-08-20, 107 cells changed and seven cell shapes appeared
 * or vanished. A hard-coded A–F map means the first inserted column silently
 * re-points every field: the Duration reader starts reading Attending, the AHP
 * reader starts reading a note. `columnMapOf` finds each field by its HEADER
 * TEXT and only falls back to the historical A–F order when no header row is
 * present.
 *
 *   OR#         e.g. "WOR 5", "WC3", "WC MRI", "TOR 2"
 *   Surgeon     surgeon NAME (not a code), may carry "+ 1" / "9 Mo, 5 yo"
 *   Procedure   free text — also the rescue slot for a room typed there
 *   Duration    time range, e.g. "730 - 1445", "0715 0815/1330- 1730"
 *   Attending   short code + parenthetical call order + optional AIC
 *   AHP (CRNA)  one or more people — see cellReader
 *
 * ⚠️ AND EVERY COLUMN PAST THE MAPPED ONES IS KEPT AS A ROW NOTE. Column G has
 * carried "First out 2 pm" / "First out 1130" / "Second out at 2" — the notes
 * that tell the AIC who leaves early — and was read by NOTHING. Anything the
 * map does not claim now rides along on the row instead of being discarded.
 */

import {
  WC_ROOM_RE,
  normalizeTime,
  parseAttendingCell,
  parseCrnaCell,
  parseCrnaCellAll,
  parseTimeRange,
  readProviderCell,
} from "./cellReader";

// The historical A–F order, used only when the grid carries no header row.
const DEFAULT_COL = { room: 0, surgeon: 1, procedure: 2, time: 3, attending: 4, crna: 5 };

// Header text → field. Matched case-insensitively against the header cell.
const HEADER_PATTERNS = [
  ["room", /^(or\s*#?|or\s*\/?\s*location|room)\b/i],
  ["surgeon", /^surgeon\b/i],
  ["procedure", /^procedure\b/i],
  ["time", /^(duration|time)\b/i],
  ["attending", /^attending\b/i],
  ["crna", /^(ahp|crna)\b/i],
];

const norm = (v) => String(v ?? "").trim();

/** True when a row is the column-header row. */
function isHeaderRow(row) {
  const joined = (row || []).map(norm).join(" ").toLowerCase();
  return /\bprocedure\b/.test(joined) && /(surgeon|attending|duration|ahp)/.test(joined);
}

/** True when a row is blank or the column-header row (skip as non-data). */
function isNonDataRow(row) {
  const cells = (row || []).map(norm);
  if (cells.every((c) => c === "")) return true;
  return isHeaderRow(row);
}

/**
 * Locate each field by its HEADER TEXT.
 *
 * @returns {{ map: object, source: "header"|"default", headerIndex: number }}
 *   `source` is reported so a caller can say WHICH answer it used — a silent
 *   fallback to positions is how a template change becomes a silent misread.
 */
export function columnMapOf(values) {
  const all = Array.isArray(values) ? values : [];
  const headerIndex = all.findIndex(isHeaderRow);
  if (headerIndex < 0) return { map: { ...DEFAULT_COL }, source: "default", headerIndex: -1 };

  const header = (all[headerIndex] || []).map(norm);
  const map = {};
  header.forEach((cell, i) => {
    if (!cell) return;
    for (const [field, re] of HEADER_PATTERNS) {
      if (map[field] === undefined && re.test(cell)) {
        map[field] = i;
        return;
      }
    }
  });
  // Any field the header did not name keeps its historical position, so a
  // partially-renamed header degrades one field at a time instead of all six.
  for (const [field, idx] of Object.entries(DEFAULT_COL)) {
    if (map[field] === undefined) map[field] = idx;
  }
  return { map, source: "header", headerIndex };
}

/**
 * Parse one facility's rooms into raw assignment rows.
 *
 * @param {Array<Array<string>>} values  — Sheets API `values.get` 2D array
 * @param {object} [opts]
 *   @param {RegExp} [opts.roomFamily=WC_ROOM_RE]  — room-prefix matcher
 * @returns {{ found, rows, columnSource, review }}
 *   rows[i] = { room, startMilitary, startHHMM, endHHMM, surgeonCode,
 *               attendingShortCode, callOrder, isAIC, crnaName, crnaShiftCode,
 *               crnaShiftNumber, crnas, notes, review, sheetRow }
 *   review  — every cell this parser could not fully read, verbatim, for the
 *             import's review queue. NEVER a reason to drop a row.
 */
export function parsePrelimAssignments(values, opts = {}) {
  const roomFamily = opts.roomFamily || WC_ROOM_RE;
  const all = Array.isArray(values) ? values : [];
  const { map: COL, source: columnSource } = columnMapOf(all);
  const mapped = new Set(Object.values(COL));

  const rows = [];
  const review = [];
  for (let i = 0; i < all.length; i += 1) {
    const raw = all[i] || [];
    if (isNonDataRow(raw)) continue;

    let room = norm(raw[COL.room]);
    // ACUTECARE (a real Tampa room) is printed in the PROCEDURE column with a
    // blank room cell on the live sheet — recover it as the row's room.
    if (!room && /^acutecare$/i.test(norm(raw[COL.procedure]))) room = "ACUTECARE";
    if (!room) continue;
    // Keep only this facility's rooms; other hospitals + footer rows drop here.
    if (!roomFamily.test(room)) continue;

    const { start: tStart, end: tEnd } = parseTimeRange(raw[COL.time]);
    const cell = readProviderCell(raw[COL.crna]);
    const crna = parseCrnaCell(raw[COL.crna]);
    const crnasAll = parseCrnaCellAll(raw[COL.crna]); // all providers in the cell
    const hasTime = Boolean(tStart || tEnd);
    // Keep a row that has a case time OR a CRNA. Timeless-with-CRNA rows are the
    // ON-CALL / bench entries (e.g. "WC OB · Grace Elaro 24", "WC TBD · Jim Slone
    // 3") — dropping them is how on-call CRNAs stopped transferring, and how a
    // blank Duration cell took its CRNA down with it.
    if (!hasTime && !crna.name) continue;

    // Every column the header map did not claim — column G's "First out 2 pm"
    // and anything else the department adds — kept verbatim on the row.
    const notes = [];
    for (let c = 0; c < raw.length; c += 1) {
      if (mapped.has(c)) continue;
      const text = norm(raw[c]);
      if (text) notes.push({ code: "extraColumn", column: c, text });
    }
    for (const n of cell.notes) notes.push(n);

    const rowReview = cell.review.map((r) => ({ ...r, room, sheetRow: i + 1 }));
    review.push(...rowReview);

    // Col B is a surgeon NAME, not a code — kept raw; resolveSurgeon will fall
    // back to the printed text when it matches no surgeon-code list.
    const surgeonName = norm(raw[COL.surgeon]);
    const att = parseAttendingCell(raw[COL.attending]);

    rows.push({
      room,
      startMilitary: tStart?.military ?? null,
      startHHMM: tStart?.hhmm ?? "",
      endHHMM: tEnd?.hhmm ?? "",
      surgeonCode: surgeonName || null,
      attendingShortCode: att.code,
      callOrder: att.callOrder,
      isAIC: att.isAIC,
      crnaName: crna.name,
      // Non-null when the reader could not vouch for the name it emitted.
      crnaNameIssue: crna.issue || null,
      crnaOrientee: crna.orienteeName || "",
      crnaOrienteeIssue: crna.orienteeIssue || null,
      // Raw code when the sheet printed a range — the SECOND provider's code
      // was carried honestly long before the seat holder's was.
      crnaOrienteeShift: crna.orienteeShiftCode ?? crna.orienteeShiftNumber ?? null,
      // ⚠️ AND WHETHER THAT CODE IS THEIRS. `cellReader` borrows the preceptor's
      // when the sheet printed no number beside the trainee. A field nobody
      // names here dies at this hop, and the board can then no longer tell an
      // assumed 3pm from a stated one.
      crnaOrienteeShiftInherited: Boolean(crna.orienteeShiftInherited),
      // ⚠️ THE SEAT HOLDER'S RAW CODE. "7p-7a" flattened to shiftNumber 7 reads
      // back as 19:00 — twelve hours early for the person holding the room.
      // Threaded exactly the way crnaOrienteeShift already is.
      crnaShiftCode: crna.shiftCode ?? (crna.shiftNumber != null ? String(crna.shiftNumber) : null),
      crnaShiftNumber: crna.shiftNumber,
      // Sheet routing annotations ("to CT", "from IR", "3 TOR16") — the
      // sheet's own arrows, used to seed Next Room links downstream.
      crnaToRoom: crna.toRoom || "",
      crnaFromRoom: crna.fromRoom || "",
      // All providers listed in the cell (multi-name cells like a bench row).
      crnas: crnasAll,
      // What the cell said besides names, and what it could not be read as.
      notes,
      review: rowReview,
      // On-call / bench: a CRNA with no case time. Drives the OB 24h card,
      // the "24 Hours / On Call" roster section, and the unassigned bench.
      onCall: !hasTime && Boolean(crna.name),
      sheetRow: i + 1,
    });
  }

  return { found: rows.length > 0, rows, columnSource, review };
}

// Re-export the shared clock helper so callers that only import this module can
// still reach it.
export { normalizeTime };
